import { useEffect, useRef, useState, useCallback } from 'react';
import type { ArticleSection } from '@/types/video-synced-article';

interface UseVideoSyncProps {
  /** Seções do artigo com timestamps */
  sections: ArticleSection[];
  /** Tempo atual do vídeo em segundos */
  currentTime: number;
  /** Se deve fazer scroll automático quando vídeo avança */
  autoScroll?: boolean;
  /** Offset do topo em pixels (para navbar fixa, etc) */
  scrollOffset?: number;
}

/**
 * Hook para sincronização bidirecional entre vídeo e artigo
 * - Video → Artigo: Rola automaticamente para seção correspondente
 * - Artigo → Video: Ao clicar em seção, avança vídeo
 */
export function useVideoSync({
  sections,
  currentTime,
  autoScroll = true,
  scrollOffset = 80,
}: UseVideoSyncProps) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const lastScrollTimeRef = useRef(0);

  /**
   * Encontra a seção ativa baseada no tempo atual do vídeo
   */
  const findActiveSectionByTime = useCallback(
    (time: number): ArticleSection | null => {
      // Encontra a seção onde o tempo está entre startTime e endTime
      // ou a última seção cujo startTime já passou
      let activeSection: ArticleSection | null = null;

      for (const section of sections) {
        if (time >= section.startTime) {
          // Se tem endTime definido, verifica se está no intervalo
          if (section.endTime && time > section.endTime) {
            continue;
          }
          activeSection = section;
        } else {
          // Já passou das seções aplicáveis
          break;
        }
      }

      return activeSection;
    },
    [sections]
  );

  /**
   * Rola suavemente para uma seção específica
   */
  const scrollToSection = useCallback(
    (sectionId: string, smooth = true) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - scrollOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: smooth ? 'smooth' : 'auto',
      });

      // Marca que estamos fazendo scroll programático
      isUserScrollingRef.current = false;
      lastScrollTimeRef.current = Date.now();
    },
    [scrollOffset]
  );

  /**
   * Navega para uma seção e retorna o tempo correspondente
   * para ser usado com seekTo do player
   */
  const navigateToSection = useCallback(
    (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return null;

      scrollToSection(sectionId, true);
      setActiveSection(sectionId);

      return section.startTime;
    },
    [sections, scrollToSection]
  );

  /**
   * Sincroniza scroll com tempo do vídeo
   */
  useEffect(() => {
    if (!autoScroll) return;

    const newActiveSection = findActiveSectionByTime(currentTime);
    if (!newActiveSection) return;

    // Só faz scroll se a seção mudou e não estamos em scroll manual
    const timeSinceLastScroll = Date.now() - lastScrollTimeRef.current;
    const sectionChanged = newActiveSection.id !== activeSection;
    const shouldScroll =
      sectionChanged &&
      !isUserScrollingRef.current &&
      timeSinceLastScroll > 1000; // Debounce de 1 segundo

    console.log('🔄 VideoSync check:', {
      currentTime: currentTime.toFixed(1),
      newSection: newActiveSection.id,
      currentSection: activeSection,
      sectionChanged,
      shouldScroll,
    });

    if (shouldScroll) {
      console.log('✅ Auto-scrolling to:', newActiveSection.id);
      setActiveSection(newActiveSection.id);
      scrollToSection(newActiveSection.id, true);
    }
  }, [currentTime, autoScroll, findActiveSectionByTime, scrollToSection, activeSection]);

  /**
   * Detecta scroll manual do usuário
   */
  useEffect(() => {
    const handleScroll = () => {
      // Marca que o usuário está fazendo scroll manual
      isUserScrollingRef.current = true;

      // Reseta o flag após um tempo
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 2000); // 2 segundos sem scroll = volta ao auto-scroll
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Detecta qual seção está visível no viewport
   */
  const updateActiveSectionByViewport = useCallback(() => {
    const scrollPosition = window.scrollY + scrollOffset + 100;

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      const element = document.getElementById(section.id);

      if (element) {
        const elementTop = element.offsetTop;

        if (scrollPosition >= elementTop) {
          setActiveSection(section.id);
          return;
        }
      }
    }
  }, [sections, scrollOffset]);

  /**
   * Observa mudanças no viewport para atualizar seção ativa
   */
  useEffect(() => {
    const handleScrollViewport = () => {
      if (isUserScrollingRef.current) {
        updateActiveSectionByViewport();
      }
    };

    window.addEventListener('scroll', handleScrollViewport, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollViewport);
    };
  }, [updateActiveSectionByViewport]);

  return {
    activeSection,
    navigateToSection,
    scrollToSection,
    findActiveSectionByTime,
  };
}
